import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTheme } from '../../context/PortalThemeContext';
import './OrderSummary.css';
import './TrackReturnPage.css';
import { ExchangeType } from './ExchangeForVariant';
import { ReturnStates } from './ReturnStates';
import InfoCard from '../shared/InfoCard';
import EditAddressModal from '../shared/EditAddressModal';
import { fetchWithSessionToken } from '../shared/fetchWithSessionToken';
import { TrackingInfoCard, TrackingProgress } from '../shared/TrackingInfo';
import SummaryItemRow from '../shared/SummaryItemRow';

const TrackReturnPage = ({
    orderDetails,
    orderReturn,
    customer,
    shopifyProducts,
    onBack,
}) => {

    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [hasItemsToAdd, setHasItemsToAdd] = useState(false);

    const returnLineItemIds = new Set(orderReturn.returnedItems?.map(retItem => retItem.lineItemId) || []);
    const itemsToReturn = (orderDetails.line_items || [])
        .filter(item => returnLineItemIds.has(String(item.id)))

    const returnedItems = orderReturn.returnedItems ?? [];
    const exchangedItems = orderReturn.exchangedItems ?? [];
    const exchangeType = exchangedItems.length > 0 ? ExchangeType.EXCHANGE : ExchangeType.REFUND;

    // Props
    const HeaderWithDetails = ({ headerText, detailTexts, marginTop }) => (
        <div key={headerText} className='tr-header-detail-item'>
            <div>{headerText}</div>
            {detailTexts && (<div className='medium-text mt-2' style={{ gap: '0.5rem' }}>
                {(detailTexts ?? []).map((text, index) => (
                    <p key={index}>{text}</p>
                ))}
            </div>)}
        </div>
    );

    function getProduct(product_id) {
        return shopifyProducts?.find((p) => p.id === product_id);
    };

    function getProductFromVariant(variant_id) {
        if (!shopifyProducts || !Array.isArray(shopifyProducts)) return null;

        return shopifyProducts.find((product) =>
            product.variants?.some((variant) => variant.id === variant_id)
        );
    }

    function getHeaderDetailText(returnType, returnStatus) {
        // TODO: update when all types/text are finalized
    };

    const AddressCard = ({ title, address }) => (
        <InfoCard
            headerText={title}
            details={[
                address.firstName + " " + address.lastName,
                address.addressLine,
                address.secondaryAddressLine,
                `${address.city}, ${address.state} ${address.zip}`,
                address.country
            ]}
            horizontal={true}
            hideImage={true}
        />
    );

    const shipping = orderReturn.shippingCost ?? 0.00;
    const returnValue = orderReturn.returnValue ?? returnedItems?.reduce((sum, item) => sum + (parseFloat(item.itemPrice || '0') * item.quantity), 0);
    const newOrderValue = orderReturn.exchangeValue ?? exchangedItems.reduce((sum, item) => sum + (parseFloat(item.itemPrice || '0') * item.quantity), 0);
    const totalAmount = orderReturn.totalAmount ?? returnValue - newOrderValue;
    const amountOwedText = orderReturn.refundMethod === "PAYMENT_DUE" ? "Payment due" : "Refund Amount";
    const isStoreCredit = orderReturn.refundMethod === 'STORE_CREDIT';
    const isPaymentDue = orderReturn.refundMethod === 'PAYMENT_DUE';
    const paymentMethodText = isStoreCredit
        ? 'Store credit'
        : isPaymentDue
            ? 'Payment required'
            : (orderDetails.maskedPaymentMethod || 'Original payment method');

    return (

        <div
            style={{
                backgroundColor: theme.background,
                padding: '2rem',
                width: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                minHeight: '100vh'
            }}
        >

            <Button
                variant="link"
                onClick={() => onBack()}
                style={{
                    position: 'absolute',
                    top: '0.5rem',
                    left: '0.5rem',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: theme.accentColor,
                    zIndex: 10
                }}
            >
                ← Back
            </Button>

            <h3 className="track-return-title">
                Track your return
            </h3>

            <div
                className='d-flex-row'
                style={{
                    backgroundColor: theme.background,
                    color: theme.textDark,
                    marginTop: '1.5rem',
                    justifyContent: 'flex-start'
                }}
            >

                {/* Column 1 - Tracking info and value summary */}
                <div style={{ flex: '3', minWidth: '20rem', maxWidth: '30rem' }}>
                    <div className='card p-3'>
                        <div className='position-relative mx-2 my-3'>
                            <TrackingProgress trackingEvents={orderReturn.trackingEvents} />
                        </div>
                        <div className="summary-card mt-3">
                            <h2 className="card-header-text mb-4">
                                Summary
                            </h2>

                            <SummaryItemRow name="Return value" amount={Math.abs(returnValue).toFixed(2)} />
                            {exchangeType === ExchangeType.EXCHANGE && (
                                <SummaryItemRow name="New order" amount={Math.abs(newOrderValue).toFixed(2)} sign='-' />
                            )}
                            <SummaryItemRow name="Return shipping" amount={Math.abs(shipping).toFixed(2)} sign='-'/>

                            <div className="divider-horizontal" />

                            <SummaryItemRow name={amountOwedText} amount={Math.abs(totalAmount).toFixed(2)} bold={true} />

                            <div className="summary-item-row">
                                <p>{isPaymentDue ? 'Payment method:' : 'Refund method:'}</p>
                                <p className='text-end'>{paymentMethodText}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Column 2 - Item and shipping address info */}
                <div style={{ flex: '5' }}>

                    {/* TODO: These header texts will probably be dynamic based on return status - will use function above */}
                    <HeaderWithDetails headerText="What you're returning" detailTexts={["Your card will be credited once package is received in returnable condition."]} />

                    <div
                        className='card'
                        style={{
                            width: '100%',
                            padding: '1rem',
                            marginTop: '1.5rem',
                        }}
                    >
                        {returnedItems.map((item, index) => (
                            <InfoCard key={`returned-item-${index}-${item.itemName}`} headerText={item.itemName} imageUrl={item.itemSrc} horizontal={true} cardStyle={false} details={[
                                `Price: $${parseFloat(item.itemPrice || 0).toFixed(2)}`,
                                `Qty: ${item.quantity ?? 0}`,
                            ]} />
                        ))}
                    </div>

                    {exchangeType === ExchangeType.EXCHANGE && (
                        <div style={{ marginTop: '1.5rem' }}>
                            <HeaderWithDetails headerText="What you'll receive" detailTexts={["Your exchange will ship once return is received in returnable condition."]} />

                            <div
                                className='card'
                                style={{
                                    padding: '1rem',
                                    width: '100%',
                                    marginTop: '1.5rem',
                                }}
                            >
                                {exchangedItems.map((item, index) => (
                                    <InfoCard key={`exchange-item-${index}-${item.itemName}`} headerText={item.itemName} imageUrl={item.itemSrc} horizontal={true} cardStyle={false} details={[
                                        `Price: $${parseFloat(item.itemPrice || 0).toFixed(2)}`,
                                        `Qty: ${item.quantity ?? 0}`,
                                    ]} />
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: '1.5rem' }}>
                        <HeaderWithDetails headerText="Shipping information" />
                    </div>

                    <div className='d-flex-row' style={{ gap: '1.5rem' }}>
                        <div className='w-100' style={{ flex: 1 }}>
                            <AddressCard title="Returning from" address={orderReturn.returnFromAddress} onEdit={{}} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <AddressCard title="Shipping to" address={orderReturn.shipToAddress} />
                        </div>
                    </div>

                </div>
            </div>

        </div>

    );
};

export default TrackReturnPage;
